<script setup lang="ts">

import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import Datepicker from "@vuepic/vue-datepicker";
import moment from "moment-timezone";

import { useAuthStore } from "@/modules/auth/store/authStore";
import { clientService } from "@/modules/clients/services/clientService";
import { getBesoinWorkerSameStatusErrorMessage, isBesoinWorkerSameStatusError } from "@/modules/companyNeed/helpers/besoinWorkerErrors";

import type { ProfileScheduleInterviewModalProps, ScheduleInterviewResponse } from "@/components/modal/Profile/scheduleInterview/types";

import Button from "@/dsComponents/buttons/Button.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Loader from "@/dsComponents/Loader.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import { TABLE_DATE_FORMAT } from "@/components/Mission/DateRangeTable/constants";
import HourInput from "@/components/Mission/DateRangeTable/HourInput/HourInput.vue";
import { getInterviewOverlapErrorMessage, isInterviewOverlapError } from "@/components/modal/Profile/scheduleInterview/helpers/interviewErrors";
import scheduleInterviewService from "@/components/modal/Profile/scheduleInterview/services/scheduleInterviewService";
import { ScheduleInterviewType } from "@/components/modal/Profile/scheduleInterview/types";

interface ContactOption {
    label: string;
    value: string;
}

const DEFAULT_DURATION_IN_MINUTES = 45;

const { t } = useI18n();
const toaster = useToast();
const authStore = useAuthStore();

const props = defineProps<ProfileScheduleInterviewModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const note = ref("");
const period = ref({
    date: null as ReturnType<typeof moment> | null,
    from: "",
    to: "",
});
const selectedPerson = ref<ContactOption | null>(null);
const isDropdownOpen = ref(false);
const errors = ref({ date: false, from: false, to: false });
const isLoading = ref(false);
const companyContacts = ref<ContactOption[]>([]);
const isLoadingContacts = ref(false);

const isAgencyMode = computed(() => props.mode === ScheduleInterviewType.AGENCY);
const isClientMode = computed(() => props.mode === ScheduleInterviewType.CLIENT);
const isRescheduleMode = computed(() => Boolean(props.existingInterview));

const modalTitle = computed(() =>
    isRescheduleMode.value
        ? t("modals.companyNeed.scheduleInterview.rescheduleTitle")
        : t("modals.companyNeed.scheduleInterview.title")
);

const currentCollaboratorOption = computed<ContactOption>(() => {
    const collaborator = authStore.currentCollaborator;
    return {
        label: t("modals.companyNeed.scheduleInterview.currentCollaborator"),
        value: String(collaborator?.id ?? ""),
    };
});

const collaboratorOptions = computed<ContactOption[]>(() => {
    const current = currentCollaboratorOption.value;
    const others = authStore.collaboratorsOptions
        .filter((c): c is { label: string; value: string } => c.value != null && c.value !== current.value)
        .map((c) => ({ label: c.label, value: c.value }));
    return [current, ...others];
});

const basePersonOptions = computed<ContactOption[]>(() => {
    if (isAgencyMode.value) return collaboratorOptions.value;
    return companyContacts.value;
});

const existingContactOption = computed<ContactOption | null>(() => {
    const contactId = props.existingInterview?.contactId;
    if (!contactId) return null;

    const knownOption = basePersonOptions.value.find((person) => person.value === contactId);
    if (knownOption) return knownOption;

    const contactName = props.existingInterview?.contactName;
    return contactName ? { label: contactName, value: contactId } : { label: contactId, value: contactId };
});

const personOptions = computed<ContactOption[]>(() => {
    const existing = existingContactOption.value;
    if (!existing) return basePersonOptions.value;
    if (basePersonOptions.value.some((person) => person.value === existing.value)) {
        return basePersonOptions.value;
    }
    return [existing, ...basePersonOptions.value];
});

const effectivePerson = computed<ContactOption | null>(() => {
    if (selectedPerson.value) return selectedPerson.value;
    if (isRescheduleMode.value) return existingContactOption.value;
    if (isAgencyMode.value) return currentCollaboratorOption.value;
    return personOptions.value[0] ?? null;
});

const profileDisplayName = computed(() => {
    if (props.workerFirstname || props.workerLastname) {
        return [props.workerFirstname, props.workerLastname].filter(Boolean).join(" ");
    }
    return t("global.profile");
});

const displayDate = computed(() => (period.value.date ? period.value.date.locale("fr").format(TABLE_DATE_FORMAT) : ""));

const fetchCompanyContacts = async () => {
    if (!props.companyId) return;
    if (props.preloadedCompanyContacts?.length) {
        companyContacts.value = props.preloadedCompanyContacts;
        return;
    }
    isLoadingContacts.value = true;
    try {
        const contacts = await clientService.fetchCompanyContacts(props.companyId);
        companyContacts.value = contacts.map((contact) => ({
            label: [contact.firstname, contact.lastname].filter(Boolean).join(" ").trim() || t("global.profile"),
            value: String(contact.id ?? ""),
        }));
    } finally {
        isLoadingContacts.value = false;
    }
};

const initPeriod = () => {
    const existingInterview = props.existingInterview;
    if (existingInterview) {
        const start = moment(existingInterview.start.padStart(4, "0"), "HHmm");
        const end = existingInterview.end
            ? moment(existingInterview.end.padStart(4, "0"), "HHmm")
            : start.clone().add(DEFAULT_DURATION_IN_MINUTES, "minutes");
        period.value = { date: moment(existingInterview.date), from: start.format("HHmm"), to: end.format("HHmm") };
        return;
    }

    const now = moment();

    const roundedMinutes = Math.ceil(now.minutes() / 10) * 10;
    now.minutes(roundedMinutes).seconds(0).milliseconds(0);
    const end = now.clone().add(DEFAULT_DURATION_IN_MINUTES, "minutes");
    period.value = { date: now, from: now.format("HHmm"), to: end.format("HHmm") };
};

const setDate = (data: Date | ReturnType<typeof moment> | null) => {
    if (!data) return;
    period.value.date = moment(data);
    errors.value.date = false;
};

const setStartHour = (value: string) => {
    const startMoment = moment(value.padStart(4, "0"), "HHmm");
    period.value.from = startMoment.format("HHmm");
    period.value.to = startMoment.clone().add(DEFAULT_DURATION_IN_MINUTES, "minutes").format("HHmm");
    errors.value.from = false;
};

const setEndHour = (value: string) => {
    const endMoment = moment(value.padStart(4, "0"), "HHmm");
    const fromNum = parseInt(period.value.from, 10);
    const toNum = parseInt(value, 10);
    if (fromNum > toNum) {
        const endMoment = moment(value.padStart(4, "0"), "HHmm");
        period.value.from = endMoment.clone().subtract(DEFAULT_DURATION_IN_MINUTES, "minutes").format("HHmm");
    }
    period.value.to = endMoment.format("HHmm");
    errors.value.to = false;
};

const handleDropdownChange = (eventName: string) => {
    if (eventName === "clickaway") {
        isDropdownOpen.value = false;
    }
};

const handleSelectPerson = (person: ContactOption) => {
    selectedPerson.value = person;
    isDropdownOpen.value = false;
};

const handleClose = () => {
    props.onCancel?.();
    emit("close");
};

const buildInterviewRequest = (date: Date): (() => Promise<ScheduleInterviewResponse>) | null => {
    const { from: start, to: end } = period.value;
    const contactId = effectivePerson.value?.value;
    if (!contactId) return null;

    if (isRescheduleMode.value) {
        const interviewId = props.existingInterview?.id;
        if (!interviewId) return null;

        return () =>
            scheduleInterviewService.rescheduleInterview(props.workerId, props.companyNeedId, interviewId, {
                date,
                start,
                end,
                contactId,
                comment: note.value,
            });
    }

    return () =>
        scheduleInterviewService.scheduleInterview(props.workerId, props.companyNeedId, {
            date,
            start,
            end,
            contactId,
            interviewType: props.mode,
            comment: note.value ?? "Nouvelle demande de rendez-vous",
        });
};

const handleScheduleInterview = async () => {
    errors.value = {
        date: !period.value.date,
        from: !period.value.from,
        to: !period.value.to,
    };
    if (Object.values(errors.value).some(Boolean)) return;

    const selectedDate = period.value.date;
    if (!selectedDate) {
        errors.value.date = true;
        return;
    }

    const interviewRequest = buildInterviewRequest(selectedDate.clone().startOf("day").utc(true).toDate());
    if (!interviewRequest) return;

    isLoading.value = true;
    try {
        const interview = await interviewRequest();
        await props.onSuccess?.({
            interview,
            contactLabel: effectivePerson.value?.label,
        });
        emit("close");
    } catch (err) {
        console.error(err);
        const message = isBesoinWorkerSameStatusError(err)
            ? getBesoinWorkerSameStatusErrorMessage(err)
            : isInterviewOverlapError(err)
                ? getInterviewOverlapErrorMessage()
                : (t("errors.Global.ServerError._default") as string);
        toaster?.open({
            message,
            type: "error",
            duration: 3000,
        });
    } finally {
        isLoading.value = false;
    }
};

onBeforeMount(() => {
    initPeriod();
    note.value = props.existingInterview?.comment ?? "";
    if (isRescheduleMode.value) {
        selectedPerson.value = existingContactOption.value;
    }
    if (isClientMode.value && props.companyId) {
        fetchCompanyContacts();
    }
});

watch(existingContactOption, (existingContact) => {
    if (!isRescheduleMode.value || !existingContact) return;
    if (!selectedPerson.value || selectedPerson.value.value === existingContact.value) {
        selectedPerson.value = existingContact;
    }
});

watch(
    () => [props.mode, props.companyId] as const,
    ([mode, companyId]) => {
        selectedPerson.value = isRescheduleMode.value ? existingContactOption.value : null;
        companyContacts.value = [];
        if (mode === ScheduleInterviewType.CLIENT && companyId) {
            fetchCompanyContacts();
        }
    },
    { immediate: false }
);
</script>

<template>
    <Modal
        class="profile-schedule-interview-modal"
        width="720px"
        content-overflow="visible"
        position="center"
        :title="modalTitle"
        :open="props.open"
        close
        @close="handleClose"
    >
        <template #default>
            <div class="profile-schedule-interview-modal__content">
                <div class="profile-schedule-interview-modal__calendar">
                    <Datepicker
                        :model-value="period.date?.toDate() ?? null"
                        :enable-time-picker="false"
                        locale="fr"
                        teleport
                        inline
                        auto-apply
                        @update:model-value="setDate"
                    />
                </div>
                <div class="profile-schedule-interview-modal__form">
                    <div class="profile-schedule-interview-modal__form-container">
                        <div class="profile-schedule-interview-modal__info-section">
                            <div class="profile-schedule-interview-modal__item">
                                <div class="profile-schedule-interview-modal__item-title">
                                    <i class="profile-schedule-interview-modal__item-title-icon ti ti-user-circle" />
                                    <div class="profile-schedule-interview-modal__item-title-label typo-xxs-medium">
                                        {{ t("global.profile") }}
                                    </div>
                                </div>
                                <ComboButton variant="ghost" readonly full-width>
                                    {{ profileDisplayName }}
                                </ComboButton>
                            </div>

                            <div class="profile-schedule-interview-modal__item">
                                <div class="profile-schedule-interview-modal__item-title">
                                    <i class="profile-schedule-interview-modal__item-title-icon ti ti-cal-user" />
                                    <div class="profile-schedule-interview-modal__item-title-label typo-xxs-medium">
                                        {{ t("modals.companyNeed.scheduleInterview.fields.contact.label") }}
                                    </div>
                                </div>
                                <ControlledDropdown
                                    class="profile-schedule-interview-modal__collaborator-dropdown"
                                    max-height="200px"
                                    :open="isDropdownOpen"
                                    @change="handleDropdownChange"
                                >
                                    <template #trigger-zone>
                                        <ComboButton
                                            variant="ghost"
                                            full-width
                                            :disabled="isClientMode && isLoadingContacts"
                                            :placeholder="!effectivePerson?.label"
                                            @click="isDropdownOpen = !isDropdownOpen"
                                        >
                                            <Loader v-if="isClientMode && isLoadingContacts" size="sm" />
                                            <template v-else>
                                                {{ effectivePerson?.label ?? t("modals.companyNeed.scheduleInterview.fields.contact.placeholder") }}
                                            </template>
                                        </ComboButton>
                                    </template>
                                    <template #dropdown-content>
                                        <DropdownItem v-for="person of personOptions" :key="person.value" @click="handleSelectPerson(person)">
                                            {{ person.label }}
                                        </DropdownItem>
                                    </template>
                                </ControlledDropdown>
                            </div>

                            <div class="profile-schedule-interview-modal__item">
                                <div class="profile-schedule-interview-modal__item-title">
                                    <i class="profile-schedule-interview-modal__item-title-icon ti ti-cal" />
                                    <div class="profile-schedule-interview-modal__item-title-label typo-xxs-medium">
                                        {{ t("form.date") }}
                                    </div>
                                </div>
                                <TextInputAlt :error="errors.date" variant="grey2" state="filled" disabled :model-value="displayDate" />
                            </div>

                            <div class="profile-schedule-interview-modal__item">
                                <div class="profile-schedule-interview-modal__item-title">
                                    <i class="profile-schedule-interview-modal__item-title-icon ti ti-clock" />
                                    <div class="profile-schedule-interview-modal__item-title-label typo-xxs-medium">
                                        {{ t("modals.dateScheduling.hour") }}
                                    </div>
                                </div>
                                <div class="profile-schedule-interview-modal__hours-row">
                                    <HourInput
                                        :error="errors.from"
                                        :value="period.from"
                                        icon=""
                                        :placeholder="t('modals.companyNeed.scheduleInterview.fields.startHour.placeholder')"
                                        :period="5"
                                        @select-hour="setStartHour"
                                    />
                                    <HourInput
                                        :error="errors.to"
                                        :value="period.to"
                                        icon="arrow-right"
                                        :placeholder="t('modals.companyNeed.scheduleInterview.fields.endHour.placeholder')"
                                        :period="5"
                                        @select-hour="setEndHour"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="profile-schedule-interview-modal__form-container">
                        <div class="profile-schedule-interview-modal__description">
                            <div class="profile-schedule-interview-modal__description-title typo-xxs-medium">
                                {{ t("modals.companyNeed.scheduleInterview.fields.comment.label") }}
                            </div>
                            <TextareaInput
                                v-model="note"
                                :placeholder="t('modals.companyNeed.scheduleInterview.fields.comment.placeholder')"
                                auto-resize
                                no-border
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="profile-schedule-interview-modal__footer">
                <Button variant="stroke" type="neutral" size="sm" :disabled="isLoading" @click="handleClose">
                    {{ t("global.actions.cancel") }}
                </Button>
                <Button size="sm" :loading="isLoading" @click="handleScheduleInterview">
                    {{ t("global.actions.save") }}
                </Button>
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.profile-schedule-interview-modal {
    &__content {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: var(--spacing-3xl, 24px);
        width: 100%;
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
        max-height: 330px;
        overflow-y: auto;

        &-container {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-lg, 12px);
        }
    }

    &__info-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md, 8px);
        width: 100%;
        padding-top: var(--spacing-md, 8px);
    }

    &__item {
        display: flex;

        &-title {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            gap: var(--spacing-sm, 6px);
            width: 124px;

            &-icon {
                flex-shrink: 0;
                font-size: 14px;
                color: var(--icon-sub-500, #636778);
            }

            &-label {
                flex: 1 0 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: var(--text-sub-600, #494e61);
            }
        }
    }

    &__hours-row {
        display: flex;
        align-items: center;
        gap: var(--spacing-md, 8px);
    }

    &__description {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs, 4px);
        width: 100%;

        &-title {
            color: var(--text-sub-600, #494e61);
        }
    }

    &__collaborator-dropdown {
        width: 100%;
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-3xl, 24px);
    }
}
</style>
